import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CatalogAPI, SupplierAPI, formatPrice, resolveImage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useAdminEdit } from '../context/AdminEditContext';
import ProductCard from '../components/ProductCard';
import ShareButtons from '../components/ShareButtons';
import { useToast } from '../hooks/use-toast';
import { MapPin, Search, ShoppingCart, Store, BadgeCheck, Truck, ChevronUp, ChevronDown, Image as ImageIcon, Pencil, Eye, EyeOff, Save, Plus } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

const PAGE_SIZE = 24;

// Carte produit déplaçable (drag & drop tactile en mode édition)
const SortableProduct = ({ p, idx, editMode, isOwner, store, cats, leafCats, catName, uploading,
  changeProductImg, updateOffer, changeCat, onAddToCart, move, pageLen }) => {
  const v = p.variants[0] || {};
  const sortable = useSortable({ id: p.id, disabled: !editMode });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-xl border border-[#E8E0D0] overflow-hidden flex flex-col">
      <div className="relative block aspect-square bg-[#FAF7F2] overflow-hidden">
        {p.image ? <img src={resolveImage(p.image)} alt={p.name} className="w-full h-full object-contain" />
          : <div className="w-full h-full flex items-center justify-center text-[#D8CFC0]"><ImageIcon className="w-10 h-10" /></div>}
        {!editMode && <Link to={`/produit/${p.id}`} className="absolute inset-0" />}
        {editMode && (
          <>
            <button {...attributes} {...listeners}
              className="absolute top-1 right-1 bg-[#1F4D3F] text-white rounded p-1.5 cursor-grab active:cursor-grabbing touch-none" title="Glisser pour déplacer">
              <GripVertical className="w-4 h-4" />
            </button>
            <label className="absolute bottom-1 left-1 bg-white/90 text-[#1F4D3F] text-[10px] px-2 py-1 rounded cursor-pointer">
              {uploading === p.id ? '...' : '📷 Photo'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => changeProductImg(p, e.target.files[0])} />
            </label>
            <button onClick={() => updateOffer(p, { visible_in_store: false })} title="Masquer"
              className="absolute top-1 left-1 bg-white/90 rounded p-1 text-[#C75B27]"><EyeOff className="w-3 h-3" /></button>
          </>
        )}
      </div>
      <div className="p-3 flex flex-col flex-1">
        {p.brand && <div className="text-[10px] uppercase tracking-wider text-[#9A8F82]">{p.brand}</div>}
        {editMode ? (
          <>
            <div className="text-sm font-medium text-[#2D2A26] line-clamp-2">{p.name}</div>
            <select value={p.category} onChange={(e) => changeCat(p, e.target.value)} className="mt-1 text-[11px] border border-[#E8E0D0] rounded px-1 py-1 bg-white">
              <option value={p.category}>{catName(p.category)}</option>
              {leafCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="mt-2 flex gap-1">
              <input type="number" defaultValue={v.price} onBlur={(e) => Number(e.target.value) !== v.price && updateOffer(p, { price: Number(e.target.value) })}
                className="w-20 text-sm border border-[#E8E0D0] rounded px-2 py-1" placeholder="Prix" />
              <input type="number" min="1" defaultValue={v.min_qty || 1} onBlur={(e) => Number(e.target.value) !== (v.min_qty || 1) && updateOffer(p, { min_qty: Number(e.target.value) })}
                className="w-14 text-sm border border-[#E8E0D0] rounded px-2 py-1" title="Qté min" />
            </div>
          </>
        ) : (
          <>
            <Link to={`/produit/${p.id}`} className="text-sm font-medium text-[#2D2A26] line-clamp-2 hover:text-[#1F4D3F]">{p.name}</Link>
            <div className="mt-auto pt-2">
              <div className="font-serif text-lg font-semibold text-[#1F4D3F]">{p.price > 0 ? formatPrice(p.price) : 'Prix sur demande'}</div>
              {v.min_qty > 1 && <div className="text-[10px] text-[#C75B27]">dès {v.min_qty}</div>}
              <button
                onClick={() => onAddToCart && onAddToCart({ id: p.id, name: p.name, image: p.image, category: p.category }, { label: v.label, price: v.price, supplier_id: store.supplier_id, min_qty: v.min_qty || 1, stock: 999 }, v.min_qty || 1)}
                disabled={!v.in_stock || p.price <= 0}
                className="mt-2 w-full bg-[#1F4D3F] hover:bg-[#173B30] disabled:bg-gray-300 text-white py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                <ShoppingCart className="w-4 h-4" /> Ajouter
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


const StorePage = ({ onAddToCart }) => {
  const { slug } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [page, setPage] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [uploading, setUploading] = useState('');
  const [savingStore, setSavingStore] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const load = () => {
    setLoading(true);
    CatalogAPI.store(slug)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  };

  useEffect(() => { load(); CatalogAPI.categories().then(setCats).catch(() => {}); }, [slug]);

  useEffect(() => {
    if (search.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      CatalogAPI.products({ q: search, limit: 8, include_empty: true }).then(setSearchResults).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#7A6F5C]">Chargement de la boutique…</div>;
  if (error || !data) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <Store className="w-12 h-12 mx-auto text-[#C75B27] mb-4" />
      <h1 className="text-2xl font-serif text-[#2D2A26] mb-2">Boutique introuvable</h1>
      <p className="text-[#7A6F5C]">Cette boutique n'existe pas ou n'est plus active.</p>
      <Link to="/" className="inline-block mt-6 text-[#1F4D3F] underline">Retour à l'accueil</Link>
    </div>
  );

  const { store, products } = data;
  const isOwner = user && user.supplier_id && user.supplier_id === store.supplier_id;
  const isAdminViewer = user && user.role === 'admin' && !isOwner;
  const catName = (id) => (cats.find(c => c.id === id) || {}).name || id;
  const leafCats = cats.filter(c => c.level === 3 || (c.level === 2 && !cats.some(x => x.parent_id === c.id)));

  const categories = ['all', ...Array.from(new Set(products.map(p => p.category)))];
  const filtered = products.filter(p =>
    (cat === 'all' || p.category === cat) &&
    (!q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.brand || '').toLowerCase().includes(q.toLowerCase()))
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ---- Actions d'édition (propriétaire) ----
  const uploadStoreImg = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const res = await SupplierAPI.uploadImage(file);
      const url = res.url || (res.file_id ? `/api/files/${res.file_id}` : '');
      await SupplierAPI.updateMe({ [field]: url });
      const k = field === 'store_logo' ? 'logo' : 'banner';
      setData((d) => ({ ...d, store: { ...d.store, [k]: url } }));
      toast({ title: 'Image mise à jour' });
    } catch (e) { toast({ title: 'Erreur', description: "Échec de l'upload" }); }
    setUploading('');
  };

  const saveStoreField = async (field, value) => {
    setSavingStore(true);
    try { await SupplierAPI.updateMe({ [field]: value }); toast({ title: 'Enregistré' }); }
    catch (e) { toast({ title: 'Erreur' }); }
    setSavingStore(false);
  };

  const patchLocal = (pid, fn) => {
    setData((d) => ({ ...d, products: d.products.map((x) => x.id === pid ? fn(x) : x) }));
  };

  const updateOffer = async (p, patch) => {
    const v = (p.variants || [])[0] || {};
    patchLocal(p.id, (x) => {
      const nv = { ...(x.variants[0] || {}) };
      if (patch.price !== undefined) { nv.price = patch.price; }
      if (patch.min_qty !== undefined) nv.min_qty = patch.min_qty;
      if (patch.visible_in_store !== undefined) nv.visible_in_store = patch.visible_in_store;
      if (patch.store_order !== undefined) nv.store_order = patch.store_order;
      const newVariants = [nv, ...x.variants.slice(1)];
      return { ...x, variants: newVariants, price: patch.price !== undefined ? patch.price : x.price };
    });
    if (patch.visible_in_store === false) {
      setData((d) => ({ ...d, products: d.products.filter((x) => x.id !== p.id) }));
    }
    try { await SupplierAPI.updateAvailability({ product_id: p.id, variant_label: v.label, ...patch }); }
    catch (e) { toast({ title: 'Erreur', description: 'Mise à jour échouée' }); load(); }
  };

  const changeProductImg = async (p, file) => {
    if (!file) return;
    setUploading(p.id);
    try {
      const res = await SupplierAPI.uploadImage(file);
      const url = res.url || (res.file_id ? `/api/files/${res.file_id}` : '');
      await SupplierAPI.setProductImage(p.id, url);
      patchLocal(p.id, (x) => ({ ...x, image: url }));
      toast({ title: 'Photo mise à jour' });
    } catch (e) { toast({ title: 'Erreur' }); }
    setUploading('');
  };

  const changeCat = async (p, category) => {
    patchLocal(p.id, (x) => ({ ...x, category }));
    try { await SupplierAPI.setProductCategory(p.id, category); } catch (e) { toast({ title: 'Erreur' }); }
  };

  const addExisting = async (prod) => {
    try {
      await SupplierAPI.addOffer({ product_id: prod.id, variants: [{ label: 'Unité', price: 0, min_qty: 1, stock: 999, in_stock: true, supplier_id: store.supplier_id }] });
      toast({ title: 'Produit ajouté' }); setSearch(''); setSearchResults([]); load();
    } catch (e) { toast({ title: 'Erreur' }); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= pageItems.length) return;
    const a = pageItems[idx], b = pageItems[j];
    const ao = (a.variants[0] || {}).store_order ?? ((page - 1) * PAGE_SIZE + idx);
    const bo = (b.variants[0] || {}).store_order ?? ((page - 1) * PAGE_SIZE + j);
    await updateOffer(a, { store_order: bo });
    await updateOffer(b, { store_order: ao });
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {/* Barre mode édition (propriétaire) */}
      {isOwner && (
        <div className="bg-[#1F4D3F] text-white text-sm">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <span className="flex items-center gap-2"><Pencil className="w-4 h-4" /> Vous êtes le propriétaire de cette boutique</span>
            <button onClick={() => setEditMode(!editMode)} className={`px-3 py-1.5 rounded-lg font-medium ${editMode ? 'bg-white text-[#1F4D3F]' : 'bg-white/20 hover:bg-white/30'}`}>
              {editMode ? '✓ Mode édition activé' : 'Activer le mode édition'}
            </button>
          </div>
        </div>
      )}

      {/* Bannière */}
      <div className="relative h-48 md:h-60 bg-gradient-to-r from-[#1F4D3F] to-[#2D6452] overflow-hidden">
        {store.banner && <img src={resolveImage(store.banner)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        {editMode && (
          <label className="absolute top-3 right-3 bg-white/90 text-[#1F4D3F] text-xs px-3 py-1.5 rounded-lg cursor-pointer hover:bg-white z-10">
            {uploading === 'store_banner' ? '...' : '📷 Changer la bannière'}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadStoreImg('store_banner', e.target.files[0])} />
          </label>
        )}
        <div className="relative max-w-7xl mx-auto px-4 h-full flex items-end pb-6">
          <div className="flex items-center gap-4">
            <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white shadow-lg flex items-center justify-center overflow-hidden shrink-0">
              {store.logo ? <img src={resolveImage(store.logo)} alt={store.name} className="w-full h-full object-cover" /> : <Store className="w-10 h-10 text-[#1F4D3F]" />}
              {editMode && (
                <label className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center cursor-pointer text-white text-[10px]">
                  {uploading === 'store_logo' ? '...' : 'Logo'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadStoreImg('store_logo', e.target.files[0])} />
                </label>
              )}
            </div>
            <div className="text-white">
              {editMode ? (
                <input defaultValue={store.name} onBlur={(e) => e.target.value !== store.name && saveStoreField('store_name', e.target.value)}
                  className="text-2xl md:text-3xl font-serif font-semibold bg-white/20 rounded px-2 py-1 text-white placeholder-white/60" />
              ) : (
                <h1 className="text-2xl md:text-3xl font-serif font-semibold flex items-center gap-2">{store.name}<BadgeCheck className="w-5 h-5 text-emerald-300" /></h1>
              )}
              <div className="flex items-center gap-3 text-sm text-white/90 mt-1 flex-wrap">
                {store.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {store.city}</span>}
                <span>★ {store.rating || 0}</span>
                <span>{products.length} produits</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {editMode ? (
          <textarea defaultValue={store.description} placeholder="Décrivez votre boutique…"
            onBlur={(e) => e.target.value !== store.description && saveStoreField('store_description', e.target.value)}
            className="w-full mb-4 px-3 py-2 border border-[#E8E0D0] rounded-lg text-sm" rows={2} />
        ) : (store.description && <p className="text-[#5A5247] mb-4">{store.description}</p>)}
        <div className="mb-4 p-3 bg-[#FAF7F2] border border-[#E8E0D0] rounded-xl">
          <div className="text-xs text-[#7A6F5C] mb-2">{isOwner ? 'Partagez votre boutique pour attirer vos clients :' : 'Partager cette boutique :'}</div>
          <ShareButtons url={`/store/${slug}`} text={`Decouvrez la boutique ${store.name} sur Comptoir Online`} compact />
        </div>

        {store.delivery_note && !editMode && (
          <div className="inline-flex items-center gap-2 text-sm bg-emerald-50 text-emerald-800 px-3 py-1.5 rounded-lg mb-4"><Truck className="w-4 h-4" /> {store.delivery_note}</div>
        )}

        {/* Ajout produit (édition) */}
        {editMode && (
          <div className="bg-white border border-[#E8E0D0] rounded-xl p-3 mb-5">
            <div className="relative">
              <Plus className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F4D3F]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ajouter un produit du catalogue…"
                className="w-full pl-9 pr-3 py-2 border border-[#E8E0D0] rounded-lg text-sm focus:outline-none" />
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 border border-[#E8E0D0] rounded-lg divide-y divide-[#F5F1EA] max-h-64 overflow-y-auto">
                {searchResults.map((r) => {
                  const already = (r.variants || []).some(v => v.supplier_id === store.supplier_id);
                  return (
                    <div key={r.id} className="flex items-center gap-3 p-2">
                      <img src={resolveImage(r.image)} alt="" className="w-9 h-9 rounded object-cover bg-[#FAF7F2]" />
                      <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{r.name}</div></div>
                      {already ? <span className="text-xs text-emerald-700">Déjà présent</span>
                        : <button onClick={() => addExisting(r)} className="bg-[#1F4D3F] text-white text-xs px-3 py-1.5 rounded">Ajouter</button>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Recherche + catégories (client) */}
        {!editMode && (
          <div className="bg-white rounded-xl border border-[#E8E0D0] p-3 mb-5 flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8F82]" />
              <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Rechercher dans cette boutique…"
                className="w-full pl-9 pr-3 py-2 border border-[#E8E0D0] rounded-lg text-sm focus:outline-none" />
            </div>
            <select value={cat} onChange={(e) => { setCat(e.target.value); setPage(1); }} className="px-3 py-2 border border-[#E8E0D0] rounded-lg text-sm bg-white">
              {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'Toutes catégories' : catName(c)}</option>)}
            </select>
          </div>
        )}

        {/* Grille produits */}
        {isAdminViewer ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {pageItems.map((p) => {
              const v = p.variants[0] || {};
              const prod = { ...p, variants: [{ label: v.label || 'Unité', price: v.price || 0, supplier_id: store.supplier_id }] };
              return <ProductCard key={p.id} product={prod} onAddToCart={onAddToCart} />;
            })}
          </div>
        ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            const ids = pageItems.map((x) => x.id);
            const oldIndex = ids.indexOf(active.id);
            const newIndex = ids.indexOf(over.id);
            if (oldIndex < 0 || newIndex < 0) return;
            const base = (page - 1) * PAGE_SIZE;
            const newIds = arrayMove(ids, oldIndex, newIndex);
            setData((d) => {
              const byId = Object.fromEntries(d.products.map((x) => [x.id, x]));
              newIds.forEach((id, i) => { if (byId[id] && byId[id].variants[0]) byId[id].variants[0].store_order = base + i; });
              const reordered = newIds.map((id) => byId[id]);
              const others = d.products.filter((x) => !newIds.includes(x.id));
              return { ...d, products: [...reordered, ...others] };
            });
            newIds.forEach((id, i) => {
              const prod = pageItems.find((x) => x.id === id);
              const v = (prod && prod.variants[0]) || {};
              SupplierAPI.updateAvailability({ product_id: id, variant_label: v.label, store_order: base + i }).catch(() => {});
            });
          }}>
          <SortableContext items={pageItems.map((x) => x.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {pageItems.map((p, idx) => (
                <SortableProduct key={p.id} p={p} idx={idx} editMode={editMode} isOwner={isOwner}
                  store={store} cats={cats} leafCats={leafCats} catName={catName} uploading={uploading}
                  changeProductImg={changeProductImg} updateOffer={updateOffer} changeCat={changeCat}
                  onAddToCart={onAddToCart} move={move} pageLen={pageItems.length} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        )}
        {filtered.length === 0 && <div className="text-center text-[#7A6F5C] py-12">Aucun produit.</div>}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-[#E8E0D0] bg-white disabled:opacity-40 text-sm">Précédent</button>
            <span className="text-sm text-[#5A5247]">Page {page} / {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg border border-[#E8E0D0] bg-white disabled:opacity-40 text-sm">Suivant</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StorePage;
